import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";

const stats = [
  { value: "120+", label: "每日赛事直播" },
  { value: "30", label: "覆盖NBA球队" },
  { value: "8", label: "内容资讯栏目" },
  { value: "24h", label: "赛事动态更新" },
];

const services = [
  {
    icon: "ri-live-line",
    title: "实时比赛直播",
    desc: "聚合每日NBA比赛直播入口，按日期与球队快速定位正在进行的比赛，随时进入观赛。",
  },
  {
    icon: "ri-calendar-line",
    title: "完整赛程查询",
    desc: "提供按日期查询的NBA赛程列表，清晰展示对阵双方、比赛时间、比赛地点与赛果简要。",
  },
  {
    icon: "ri-newspaper-line",
    title: "赛事新闻资讯",
    desc: "汇总战报、前瞻、数据解读与联盟动态，帮助球迷从多角度了解NBA最新情况。",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "赛况与数据回顾",
    desc: "展示比分走势与关键事件简报，方便球迷回顾比赛进程中的关键节点。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "商务与内容合作", value: "contact@nba-zhibo.com" },
  { icon: "ri-customer-service-2-line", label: "用户反馈", value: "support@nba-zhibo.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "NBA直播吧" },
  { icon: "ri-time-line", label: "客服时间", value: "每日 09:00 - 23:00" },
];

export default function About() {
  useSeo({
    title: "NBA直播吧 - 关于我们",
    description:
      "了解NBA直播吧的网站定位、内容范围与服务承诺。我们专注为篮球迷提供实时、全面的NBA赛事直播、赛程与新闻资讯。",
    keywords: "NBA直播吧,关于我们,NBA直播平台,篮球资讯,免责声明",
  });

  return (
    <div className="w-full">
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_15%_20%,oklch(var(--primary-700)),transparent_55%),radial-gradient(circle_at_85%_80%,oklch(var(--accent-800)),transparent_55%)]"></div>
        <div className="relative w-full px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-xs text-primary-300 font-label mb-3">
            <i className="ri-home-4-line"></i>
            <Link to="/" className="hover:text-accent-400 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-primary-100">关于我们</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 max-w-2xl leading-tight">
            关于 <span className="text-accent-500">NBA直播吧</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-primary-200 leading-relaxed">
            我们是一群热爱篮球的内容团队，致力于为NBA球迷提供实时、全面的赛事直播入口、赛程查询与新闻资讯，让每一次观赛都更简单、更尽兴。
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {stats.map((s) => (
              <div key={s.label} className="bg-background-50/10 border border-background-50/15 rounded-lg px-4 py-3 backdrop-blur-sm">
                <div className="font-heading text-2xl md:text-3xl font-bold text-accent-400">{s.value}</div>
                <div className="text-xs text-primary-200 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">网站介绍</h2>
            </div>
            <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                NBA直播吧是一个面向篮球爱好者的体育直播门户网站，主题聚焦NBA篮球比赛的实时直播与赛程信息。我们围绕比赛直播、赛程查询、赛事新闻与比赛回顾四大方向组织内容，帮助球迷快速找到想看的信息。
              </p>
              <p>
                网站以信息整洁、重点突出为设计原则，兼顾PC端与手机端的阅读体验。首页以焦点直播与当日赛程为核心，右侧辅以最新赛事新闻；分类页提供日期与球队筛选，赛程页支持按日期快速切换，详情页则集中呈现比赛信息与赛况简报。
              </p>
              <p>
                我们希望成为球迷日常观赛的信息入口，让“找直播、查赛程、看新闻”变得轻松直接。
              </p>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg overflow-hidden border border-background-200 bg-background-100 h-64 lg:h-full min-h-[240px]">
              <img
                src="https://readdy.ai/api/search-image?query=Empty%20modern%20basketball%20court%20inside%20a%20dimly%20lit%20arena%20with%20spotlights%20and%20blue%20tones%2C%20clean%20architectural%20composition%2C%20cinematic%20photography%2C%20high%20detail&width=800&height=900&seq=nba-about-01&orientation=portrait"
                alt="NBA直播吧 比赛场馆"
                title="NBA直播吧 - 专业篮球赛事直播平台"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-10 md:pb-14">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">内容范围与服务承诺</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s) => (
            <div key={s.title} className="bg-background-50 rounded-lg border border-background-200 p-5">
              <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center text-2xl mb-4">
                <i className={s.icon}></i>
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-secondary-50 rounded-lg border border-secondary-200 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
            </div>
            <ul className="flex flex-col gap-4">
              {contacts.map((c) => (
                <li key={c.label} className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-lg bg-background-50 text-secondary-600 flex items-center justify-center text-xl">
                    <i className={c.icon}></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{c.label}</div>
                    <div className="text-sm font-medium text-foreground-900">{c.value}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-secondary-200 flex items-center gap-3">
              <span className="text-xs text-foreground-500">关注我们：</span>
              {[
                { icon: "ri-weibo-fill", label: "微博" },
                { icon: "ri-wechat-fill", label: "微信" },
                { icon: "ri-youtube-fill", label: "视频号" },
              ].map((s) => (
                <span
                  key={s.label}
                  title={s.label}
                  className="w-9 h-9 rounded-md bg-background-50 border border-secondary-200 text-secondary-600 flex items-center justify-center text-lg cursor-pointer hover:border-secondary-400 hover:text-secondary-800 transition-colors"
                >
                  <i className={s.icon}></i>
                </span>
              ))}
            </div>
          </div>

          <div className="bg-accent-50 rounded-lg border border-accent-100 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">免责声明</h2>
            </div>
            <div className="flex flex-col gap-3 text-sm text-accent-900 leading-relaxed">
              <p className="flex items-start gap-2">
                <i className="ri-information-line mt-0.5 shrink-0"></i>
                本站所有比赛直播、比分、赛况与新闻内容仅供球迷交流参考，不构成任何形式的官方信息。
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-live-line mt-0.5 shrink-0"></i>
                直播区域为前端演示界面，未接入真实直播信号，站内比赛与新闻数据均为模拟示例。
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-copyright-line mt-0.5 shrink-0"></i>
                本站与NBA官方无隶属关系，相关球队名称与标识仅用于信息描述，版权归各自所有者所有。
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-shield-check-line mt-0.5 shrink-0"></i>
                如本站内容无意中侵犯了您的权益，请通过上述联系方式告知，我们将及时处理。
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}